import type { enums, types, UserAccountType } from "@fraction/shared";
import { notifyManager, type QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type { ChecklistApp } from "../api/types";
import { selectChecklistApplication } from "../selectors/application";
import { useFractionClient } from "./FractionClientContext";

/** Prefix for application LIST queries (ArrayResponse). */
export const APPLICATION_KEY = ["application"] as const;
/** Prefix for SINGLE application queries — `[...SINGLE_APPLICATION_KEY, id]`. */
export const SINGLE_APPLICATION_KEY = ["single_application"] as const;
export const CHECKLIST_KEY = ["checklist"] as const;

/**
 * The lite "id" slot, holding barebones list rows used only as placeholder data
 * by single-application queries. Kept separate from the real
 * `[...SINGLE_APPLICATION_KEY, id]` detail slot so a partial list row can never
 * be mistaken for the complete application graph.
 */
export const liteApplicationKey = (id: string) => [...SINGLE_APPLICATION_KEY, "lite", id];

/**
 * Optimistically patch an application across every cache that holds it — the
 * list responses (`APPLICATION_KEY` ArrayResponses) and the single-application
 * entry (`[...SINGLE_APPLICATION_KEY, id]`), plus the borrower "ApplicantApplication"
 * variant. The single source of truth for application cache writes, shared by
 * web and mobile. `setQueriesData` no-ops on caches a platform doesn't keep.
 */
export const updateApplicationInFetchCache = (
  queryClient: QueryClient,
  applicationId: string,
  // Loose return (matches the original web signature) so the many web call
  // sites that return partial/plain objects type-check unchanged.
  updater: (prevApp?: ChecklistApp | null) => any
) => {
  return notifyManager.batch(() => {
    queryClient.setQueriesData<types.ArrayResponse<ChecklistApp>>(
      { exact: false, queryKey: APPLICATION_KEY },
      (prev) =>
        ({
          ...(prev || {}),
          data: prev?.data?.map((app) =>
            app.id === applicationId ? selectChecklistApplication(updater(app)) : app
          ),
        }) as types.ArrayResponse<ChecklistApp>
    );

    // Normalize to an Application instance so the per-subscriber `select`
    // (selectChecklistApplication) hits its instance fast-path on reads.
    const updated = queryClient.setQueryData<ChecklistApp>(
      [...SINGLE_APPLICATION_KEY, applicationId],
      (prev) => selectChecklistApplication(updater(prev))
    );

    queryClient.setQueriesData<ChecklistApp>(
      { exact: false, queryKey: [...SINGLE_APPLICATION_KEY, "ApplicantApplication"] },
      (prev?: ChecklistApp | null) =>
        prev?.id === applicationId ? selectChecklistApplication(updater(prev)) : (prev ?? undefined)
    );

    return updated;
  });
};

/**
 * Remove an application from every cache that can expose it. Deletion cannot
 * use `updateApplicationInFetchCache(..., () => null)`: list caches would retain
 * a null row and consumers reasonably expect every row to be an application.
 */
export const removeApplicationFromFetchCache = (queryClient: QueryClient, applicationId: string) => {
  notifyManager.batch(() => {
    queryClient.setQueriesData<types.ArrayResponse<ChecklistApp>>(
      { exact: false, queryKey: APPLICATION_KEY },
      (prev) => {
        if (!prev?.data) {
          return prev;
        }
        const data = prev.data.filter((app) => app && app.id !== applicationId);
        return data.length === prev.data.length ? prev : { ...prev, data };
      }
    );

    queryClient.removeQueries({
      exact: true,
      queryKey: [...SINGLE_APPLICATION_KEY, applicationId],
    });
    queryClient.removeQueries({
      exact: true,
      queryKey: liteApplicationKey(applicationId),
    });
    queryClient.setQueriesData<ChecklistApp>(
      { exact: false, queryKey: [...SINGLE_APPLICATION_KEY, "ApplicantApplication"] },
      (prev) => (prev?.id === applicationId ? undefined : prev)
    );
  });
};

/** Immutably set a (possibly dotted) field path on a plain object/clone. */
function setNestedField(obj: any, path: string, value: unknown): any {
  const keys = path.split(".");
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: value };
  }
  const [head, ...rest] = keys;
  return { ...obj, [head]: setNestedField(obj?.[head] ?? {}, rest.join("."), value) };
}

/**
 * Optimistically patch a single field on a nested entity (applicant / debt /
 * income / expense / financialAsset) inside the application tree, in every
 * cache that holds the application. The nested-entity sibling of
 * `updateApplicationInFetchCache`; powers the proposed-update apply/revert
 * flow. Shared so the cache write lives next to the (shared) client methods
 * that drive it.
 */
export const updateEntityFieldInFetchCache = (
  queryClient: QueryClient,
  applicationId: string,
  entityType: string,
  entityId: string,
  field: string,
  newValue: unknown
) => {
  const updateById = <T extends { id?: string }>(items: T[] | undefined | null): T[] | undefined | null => {
    if (!items) return items;
    return items.map((item) => (item.id === entityId ? setNestedField(item, field, newValue) : item));
  };

  updateApplicationInFetchCache(queryClient, applicationId, (prev) => {
    if (!prev) return prev;

    switch (entityType) {
      case "application":
        return prev.id === entityId ? setNestedField(prev, field, newValue) : prev;

      case "applicant":
        return { ...prev, applicants: updateById(prev.applicants) } as ChecklistApp;

      case "debt":
        return {
          ...prev,
          applicants: prev.applicants?.map((ap) => ({
            ...ap,
            debts: updateById(ap.debts),
            properties: ap.properties?.map((p) => ({ ...p, debts: updateById(p.debts) })),
            homeAddress: ap.homeAddress
              ? { ...ap.homeAddress, debts: updateById(ap.homeAddress.debts) }
              : ap.homeAddress,
          })),
          property: prev.property
            ? { ...prev.property, debts: updateById(prev.property.debts) }
            : prev.property,
        } as ChecklistApp;

      case "income":
        return {
          ...prev,
          applicants: prev.applicants?.map((ap) => ({
            ...ap,
            incomes: updateById(ap.incomes),
            properties: ap.properties?.map((p) => ({ ...p, incomes: updateById(p.incomes) })),
            homeAddress: ap.homeAddress
              ? { ...ap.homeAddress, incomes: updateById(ap.homeAddress.incomes) }
              : ap.homeAddress,
          })),
          property: prev.property
            ? { ...prev.property, incomes: updateById(prev.property.incomes) }
            : prev.property,
        } as ChecklistApp;

      case "expense":
        return {
          ...prev,
          applicants: prev.applicants?.map((ap) => ({
            ...ap,
            expenses: updateById(ap.expenses),
            properties: ap.properties?.map((p) => ({ ...p, expenses: updateById((p as any).expenses) })),
            homeAddress: ap.homeAddress
              ? { ...ap.homeAddress, expenses: updateById((ap.homeAddress as any).expenses) }
              : ap.homeAddress,
          })),
          property: prev.property
            ? { ...prev.property, expenses: updateById((prev.property as any).expenses) }
            : prev.property,
        } as ChecklistApp;

      case "financialAsset":
        return {
          ...prev,
          applicants: prev.applicants?.map((ap) => ({ ...ap, assets: updateById(ap.assets) })),
        } as ChecklistApp;

      default:
        return prev;
    }
  });
};

/**
 * Fetch a single application (with embedded checklists) and seed each
 * checklist into the per-status checklist cache so `useChecklist(appId,
 * status)` reads can short-circuit.
 *
 * Shared between web and mobile. Pass `enabled: false` (or omit the id) to
 * skip the fetch until you have what you need.
 */
export function useApplication(
  id: string | undefined,
  { enabled = true, userType }: { enabled?: boolean; userType?: UserAccountType } = {}
) {
  const client = useFractionClient();
  const queryClient = useQueryClient();

  return useQuery<ChecklistApp | undefined>({
    queryKey: [...SINGLE_APPLICATION_KEY, id],
    enabled: !!id && enabled,
    // Show the barebones list row instantly while the full graph loads. Lite
    // list rows seed the sibling lite slot (see `seedApplicationFromList`), so
    // they can only ever arrive here as placeholder data — `isPlaceholderData`
    // tells consumers the embedded entities can't be trusted yet, and the full
    // fetch always runs.
    placeholderData: () => (id ? queryClient.getQueryData<ChecklistApp>(liteApplicationKey(id)) : undefined),
    queryFn: async () => {
      const app = await client.getApplication(id!, userType ? { accountType: userType } : undefined);
      if (app?.checklists) {
        for (const [status, checklist] of Object.entries(app.checklists)) {
          if (!checklist) continue;
          queryClient.setQueryData([...CHECKLIST_KEY, id, status], checklist);
        }
      }
      return app;
    },
  });
}

/**
 * In-place merge of a lite list row's top-level fields over a previously
 * cached app, preserving any embedded entities the previous copy already has.
 * `_isLite` only survives if BOTH sides are lite.
 */
export const liteMerge = (prev: ChecklistApp, app: ChecklistApp) => {
  return Object.assign(prev, {
    _isLite: app?._isLite && prev?._isLite,
    status: app?.status || prev?.status,
    syntheticStatus: app?.syntheticStatus || prev?.syntheticStatus,
    updatedAt: app?.updatedAt || prev?.updatedAt,
    lenderId: app?.lenderId || prev?.lenderId,
    appraisalFileId: app?.appraisalFileId || prev?.appraisalFileId,
    approvedAmount: app?.approvedAmount || prev?.approvedAmount,
    requestedAmount: app?.requestedAmount || prev?.requestedAmount,
    downpaymentAmount: app?.downpaymentAmount || prev?.downpaymentAmount,
    holdbackPaymentAmount: app?.holdbackPaymentAmount || prev?.holdbackPaymentAmount,
    confirmedLoanAmount: app?.confirmedLoanAmount || prev?.confirmedLoanAmount,
    defaultPaymentFee: app?.defaultPaymentFee || prev?.defaultPaymentFee,
    notes: app?.notes || prev?.notes,
    underwritingNotes: app?.underwritingNotes || prev?.underwritingNotes,
    titleNotes: app?.titleNotes || prev?.titleNotes,
    dealPurpose: app?.dealPurpose || prev?.dealPurpose,
    appraisalOrderedDate: app?.appraisalOrderedDate || prev?.appraisalOrderedDate,
    requestedCloseDate: app?.requestedCloseDate || prev?.requestedCloseDate,
    interestType: app?.interestType || prev?.interestType,
    crmId: app?.crmId || prev?.crmId,
    termLength: app?.termLength || prev?.termLength,
    brokerWaivedAppraisalFees: app?.brokerWaivedAppraisalFees || prev?.brokerWaivedAppraisalFees,
    brokerWaivedInspectionFees: app?.brokerWaivedInspectionFees || prev?.brokerWaivedInspectionFees,
    preFundingGDS: app?.preFundingGDS || prev?.preFundingGDS,
    preFundingTDS: app?.preFundingTDS || prev?.preFundingTDS,
    postFundingGDS: app?.postFundingGDS || prev?.postFundingGDS,
    postFundingTDS: app?.postFundingTDS || prev?.postFundingTDS,
    portfolio: app?.portfolio || prev?.portfolio,
    commitmentLetterWithCurrentRateWasSentDate:
      app?.commitmentLetterWithCurrentRateWasSentDate || prev?.commitmentLetterWithCurrentRateWasSentDate,
    amortizationMonths: app?.amortizationMonths || prev?.amortizationMonths,
    rateType: app?.rateType || prev?.rateType,
    variableInterestRatePrimeSpread:
      app?.variableInterestRatePrimeSpread || prev?.variableInterestRatePrimeSpread,
    earlyPayoutType: app?.earlyPayoutType || prev?.earlyPayoutType,
    requestedRate: app?.requestedRate || prev?.requestedRate,
    termLengthMonths: app?.termLengthMonths || prev?.termLengthMonths,
    productId: app?.productId || prev?.productId,
    paymentFrequency: app?.paymentFrequency || prev?.paymentFrequency,
    paymentAmount: app?.paymentAmount || prev?.paymentAmount,
    notificationsFromWhitelabelLender:
      app?.notificationsFromWhitelabelLender || prev?.notificationsFromWhitelabelLender,
    isInterAliaRequest: app?.isInterAliaRequest || prev?.isInterAliaRequest,
    status_change_broker_collecting_documents:
      app?.status_change_broker_collecting_documents || prev?.status_change_broker_collecting_documents,
    status_change_lender_matching: app?.status_change_lender_matching || prev?.status_change_lender_matching,
    status_change_underwriting: app?.status_change_underwriting || prev?.status_change_underwriting,
    status_change_for_broker_review:
      app?.status_change_for_broker_review || prev?.status_change_for_broker_review,
    status_change_for_submit: app?.status_change_for_submit || prev?.status_change_for_submit,
    status_change_approved_for_commitment_letter:
      app?.status_change_approved_for_commitment_letter || prev?.status_change_approved_for_commitment_letter,
    status_change_commitment_letter_sent:
      app?.status_change_commitment_letter_sent || prev?.status_change_commitment_letter_sent,
    status_change_receiving_docs: app?.status_change_receiving_docs || prev?.status_change_receiving_docs,
    // The two anchors document typing reads. Without them the lifecycle prior silently degrades to
    // "no opinion" on the placeholder path, which looks like the model getting worse rather than
    // like missing input.
    status_change_conveyancer_engaged:
      app?.status_change_conveyancer_engaged || prev?.status_change_conveyancer_engaged,
    status_change_funded: app?.status_change_funded || prev?.status_change_funded,
    createdAt: app?.createdAt || prev?.createdAt,
    eid: app?.eid || prev?.eid,
    lendeskApplicationId: app?.lendeskApplicationId || prev?.lendeskApplicationId,
    slackChannelId: app?.slackChannelId || prev?.slackChannelId,
    id: app?.id || prev?.id,
    // Presence, not truthiness: `type` is nullable now, and `||` would resurrect a stale
    // cached type over a response that legitimately cleared it. `undefined` still means "this
    // projection didn't select the field", which is when the previous value should survive.
    type: app?.type !== undefined ? app.type : prev?.type,
    compoundFrequency: app?.compoundFrequency || prev?.compoundFrequency,
  });
};

/**
 * Seed a list response into the placeholder-only cache. Every application list
 * response is a projection: even the larger employee shape is redacted by the
 * backend and omits nested fields such as applicant home addresses. It must
 * therefore never satisfy or overwrite the full-detail query.
 */
export const seedApplicationFromList = (client: QueryClient, app: ChecklistApp) => {
  if (!app?.id) return;
  client.setQueryData<ChecklistApp>(liteApplicationKey(app.id), (prev) =>
    prev ? liteMerge(prev, app) : app
  );
};

/**
 * Fetch the application list. Pass `lite: true` for the mobile/pipeline
 * lite shape (no embedded entities — just enough to render a row).
 *
 * Side-effect: seeds each returned app into the single-application caches
 * (via {@link seedApplicationFromList}) so subsequent `useApplication(id)`
 * calls render instantly while a fresh fetch is in flight.
 */
export function useApplications({
  accountType,
  status,
  offset,
  limit,
  since,
  lite,
  brand,
  enabled = true,
}: {
  accountType?: "employee" | "broker" | "applicant" | "conveyancer" | "lender";
  status?: "active" | "all" | "closed" | "realization" | "funded";
  offset?: number;
  limit?: number;
  since?: number;
  lite?: boolean;
  /** Brand pool for the broker pipeline; omit to see every deal. */
  brand?: string;
  enabled?: boolean;
} = {}) {
  const client = useFractionClient();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [...APPLICATION_KEY, accountType, status, lite, offset, limit, since, brand],
    enabled,
    queryFn: async () => {
      const response = await client.getApplications({
        accountType,
        status,
        offset,
        limit,
        since,
        lite,
        brand,
      });
      for (const app of response?.data || []) {
        seedApplicationFromList(queryClient, app);
      }
      return response;
    },
  });
}

/**
 * Fetch a single status' checklist for an application. Shared between web
 * and mobile. The result is the same shape on both platforms because the
 * shared FractionClient normalizes the response.
 */
export function useChecklist(
  appId: string | undefined,
  status: enums.ApplicationStatus | enums.LoanStatus | undefined,
  { enabled = true, accountType }: { enabled?: boolean; accountType?: UserAccountType } = {}
) {
  const client = useFractionClient();
  const queryClient = useQueryClient();

  const query = useQuery<types.ChecklistResult[]>({
    queryKey: [...CHECKLIST_KEY, appId, status],
    enabled: !!appId && !!status && enabled,
    queryFn: () => client.getAppChecklist(appId!, status!, accountType),
  });

  // Bypass the server's checklist cache (refresh=true forces a synchronous
  // recompute) and write the fresh result into the query slot. Post-mutation
  // refetches MUST use this instead of refetch(): a plain refetch serves the
  // cached row, which can clobber an optimistic update.
  const hardRefresh = useCallback(async () => {
    if (!appId || !status) return undefined;
    const checklist = await client.getAppChecklist(appId, status, accountType, { refresh: true });
    queryClient.setQueryData([...CHECKLIST_KEY, appId, status], checklist);
    return checklist;
  }, [appId, status, accountType, client, queryClient]);

  return { ...query, hardRefresh };
}
